/**
 * MicrophoneCheckService - Utility to check microphone availability and permissions
 * Provides detailed error states for better user feedback
 */

export const MicrophoneStatus = {
  AVAILABLE: 'AVAILABLE',
  NO_DEVICES: 'NO_DEVICES',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  PERMISSION_PROMPT_REQUIRED: 'PERMISSION_PROMPT_REQUIRED',
  DEVICE_IN_USE: 'DEVICE_IN_USE',
  UNSUPPORTED: 'UNSUPPORTED',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

/** Near-zero RMS floor — practical "0 amplitude" (exact float 0 is unreliable). */
export const NEAR_ZERO_RMS = 0.002;

/** Silence duration before auto-pause while recording (ms). */
export const SILENCE_TIMEOUT_MS = 15000;

const LAST_DEVICE_STORAGE_KEY = 'scribe-last-mic-device-id';
const DEFAULT_AUDIO_CONSTRAINTS = {
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true
};

export const MicrophoneCheckService = {
  isMediaDevicesSupported() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  },

  getLastDeviceId() {
    try {
      return localStorage.getItem(LAST_DEVICE_STORAGE_KEY) || null;
    } catch {
      return null;
    }
  },

  setLastDeviceId(deviceId) {
    if (!deviceId) return;
    try {
      localStorage.setItem(LAST_DEVICE_STORAGE_KEY, deviceId);
    } catch {
      // ignore quota / private mode
    }
  },

  /**
   * Enumerate audio input devices (labels require prior permission in most browsers).
   * @returns {Promise<MediaDeviceInfo[]>}
   */
  async enumerateAudioInputs() {
    if (!this.isMediaDevicesSupported()) return [];
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === 'audioinput');
  },

  /**
   * Build getUserMedia constraints for an optional device.
   * @param {string|null} deviceId
   * @param {object} [extra]
   */
  buildAudioConstraints(deviceId = null, extra = {}) {
    const audio = {
      ...DEFAULT_AUDIO_CONSTRAINTS,
      ...extra
    };
    if (deviceId) {
      audio.deviceId = { exact: deviceId };
    }
    return { audio };
  },

  /**
   * Open a microphone stream for a specific device (or default).
   * @param {string|null} deviceId
   * @returns {Promise<MediaStream>}
   */
  async getUserMediaForDevice(deviceId = null) {
    if (!this.isMediaDevicesSupported()) {
      throw Object.assign(new Error('Media devices unsupported'), { name: 'NotSupportedError' });
    }
    try {
      return await navigator.mediaDevices.getUserMedia(this.buildAudioConstraints(deviceId));
    } catch (error) {
      // Fallback without exact device constraint (e.g. stale deviceId)
      if (deviceId && (error.name === 'OverconstrainedError' || error.name === 'NotFoundError')) {
        return navigator.mediaDevices.getUserMedia(this.buildAudioConstraints(null));
      }
      throw error;
    }
  },

  /**
   * Compute RMS from an AnalyserNode time-domain buffer.
   * @param {AnalyserNode} analyser
   * @param {Uint8Array} [buffer]
   * @returns {number} 0..1-ish normalized RMS
   */
  computeRms(analyser, buffer) {
    if (!analyser) return 0;
    const data = buffer || new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    let sumSquares = 0;
    for (let i = 0; i < data.length; i++) {
      const normalized = (data[i] - 128) / 128;
      sumSquares += normalized * normalized;
    }
    return Math.sqrt(sumSquares / data.length);
  },

  isNearZeroRms(rms) {
    return rms < NEAR_ZERO_RMS;
  },

  /**
   * Check microphone availability and permissions.
   * Always verifies with getUserMedia when permission is not denied — do not trust
   * permissions.query('granted') alone (stream may still fail or be silent).
   * @returns {Promise<{ status: string, message: string, canRecord: boolean }>}
   */
  async checkMicrophoneStatus() {
    if (!this.isMediaDevicesSupported()) {
      return {
        status: MicrophoneStatus.UNSUPPORTED,
        message: 'Your browser does not support audio recording. Please use a modern browser like Chrome, Firefox, or Edge.',
        canRecord: false
      };
    }

    try {
      const audioInputDevices = await this.enumerateAudioInputs();

      if (audioInputDevices.length === 0) {
        return {
          status: MicrophoneStatus.NO_DEVICES,
          message: 'No microphone detected. Please connect a microphone to your computer.',
          canRecord: false
        };
      }

      if (navigator.permissions && navigator.permissions.query) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
          if (permissionStatus.state === 'denied') {
            return {
              status: MicrophoneStatus.PERMISSION_DENIED,
              message: 'Microphone access is blocked. Please enable microphone permissions in your browser settings.',
              canRecord: false
            };
          }
        } catch {
          // Some browsers don't support permissions API for microphone
        }
      }

      try {
        const testStream = await this.getUserMediaForDevice(this.getLastDeviceId());
        testStream.getTracks().forEach((track) => track.stop());
        return {
          status: MicrophoneStatus.AVAILABLE,
          message: 'Microphone is ready',
          canRecord: true
        };
      } catch (getUserMediaError) {
        return this.handleGetUserMediaError(getUserMediaError);
      }
    } catch (error) {
      console.error('Error checking microphone status:', error);
      return {
        status: MicrophoneStatus.UNKNOWN_ERROR,
        message: 'Unable to check microphone status. Please try again.',
        canRecord: false
      };
    }
  },

  /**
   * Handle getUserMedia errors and provide specific messages
   * @param {Error} error
   * @returns {{ status: string, message: string, canRecord: boolean }}
   */
  handleGetUserMediaError(error) {
    const errorName = error.name;
    const errorMessage = error.message?.toLowerCase() || '';

    if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
      return {
        status: MicrophoneStatus.PERMISSION_DENIED,
        message: 'Microphone access was denied. Please enable microphone permissions in your browser settings and refresh the page.',
        canRecord: false
      };
    }

    if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
      return {
        status: MicrophoneStatus.NO_DEVICES,
        message: 'No microphone detected. Please connect a microphone to your computer.',
        canRecord: false
      };
    }

    if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
      return {
        status: MicrophoneStatus.DEVICE_IN_USE,
        message: 'Microphone is already in use by another application. Please close other applications using the microphone.',
        canRecord: false
      };
    }

    if (errorName === 'AbortError') {
      return {
        status: MicrophoneStatus.UNKNOWN_ERROR,
        message: 'Microphone access was aborted. Please try again.',
        canRecord: false
      };
    }

    if (errorName === 'OverconstrainedError') {
      return {
        status: MicrophoneStatus.UNKNOWN_ERROR,
        message: 'Your microphone does not meet the required audio specifications. Please try a different microphone.',
        canRecord: false
      };
    }

    if (errorMessage.includes('permission') || errorMessage.includes('denied')) {
      return {
        status: MicrophoneStatus.PERMISSION_DENIED,
        message: 'Microphone access was denied. Please enable microphone permissions in your browser settings.',
        canRecord: false
      };
    }

    if (errorMessage.includes('not found') || errorMessage.includes('no device')) {
      return {
        status: MicrophoneStatus.NO_DEVICES,
        message: 'No microphone detected. Please connect a microphone to your computer.',
        canRecord: false
      };
    }

    return {
      status: MicrophoneStatus.UNKNOWN_ERROR,
      message: `Unable to access microphone: ${error.message || 'Unknown error'}`,
      canRecord: false
    };
  }
};

export default MicrophoneCheckService;
